// Settings card (Session BBN): scheduled reports.
//
// A schedule asks the platform to make a report on a rhythm and leave it as a DRAFT in Gmail, addressed to the person you name.
// Nothing is ever sent: you open the draft in Gmail, read it, then send it or delete it. Each row shows what the last attempt
// did. A run that was skipped (Google was not connected) or failed made NO draft, and the row says so in words: it is never
// shown as done. The compliance reports are not offered here, because they are for auditors and are generated by hand.
// office and admin see the card (the routes are admin and office only).
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { ReportScheduleItem, ReportScheduleList } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";

const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";
const saveCls = "rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green disabled:opacity-50";
const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";

const EMAIL = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;

/** What the last run did, in plain English. A skipped or failed run is never worded as delivered. */
export function lastRunText(s: Pick<ReportScheduleItem, "lastRunAt" | "lastRunStatus" | "lastRunMessage">): { text: string; tone: "ok" | "warn" | "none" } {
  if (!s.lastRunAt || !s.lastRunStatus) return { text: "Has not run yet.", tone: "none" };
  const when = formatDateTime(s.lastRunAt);
  if (s.lastRunStatus === "drafted") return { text: `Draft made in Gmail on ${when}. Nothing was sent.`, tone: "ok" };
  if (s.lastRunStatus === "skipped") return { text: `Skipped on ${when}, so NO draft was made. ${s.lastRunMessage ?? ""}`.trim(), tone: "warn" };
  return { text: `Failed on ${when}, so NO draft was made. ${s.lastRunMessage ?? ""}`.trim(), tone: "warn" };
}

export function ReportSchedulesSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [data, setData] = useState<ReportScheduleList | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [newType, setNewType] = useState("");
  const [newCadence, setNewCadence] = useState("weekly");
  const [newRecipient, setNewRecipient] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editCadence, setEditCadence] = useState("");
  const [editRecipient, setEditRecipient] = useState("");
  const [toDelete, setToDelete] = useState<ReportScheduleItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function reload() {
    const v = await api.reportSchedules.list();
    setData(v);
    setNewType((t) => t || v.reportTypes[0]?.value || "");
  }

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.reportSchedules.list())
      .then((v) => {
        if (!live) return;
        setData(v);
        setNewType((t) => t || v.reportTypes[0]?.value || "");
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function act(key: string, fn: () => Promise<unknown>, done: (r: unknown) => string, fallback: string) {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      const r = await fn();
      await reload();
      setMessage(done(r));
    } catch (e) {
      setError(friendlyApiError(e, fallback));
    } finally {
      setBusy(null);
    }
  }

  const recipientOk = EMAIL.test(newRecipient.trim());

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Scheduled reports">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Scheduled reports</h2>
      <p className="mb-3 text-sm text-ele-body">
        Ask for a report on a rhythm and it is left as a draft in Gmail, made out to the address you give. Nothing is ever sent for you:
        open the draft, read it, then send it or delete it. A new schedule makes its first draft at the start of the next day, week or
        month. The compliance reports for auditors are not offered here.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !data ? (
        <p role="status" className="text-sm text-ele-strong">
          Could not load the scheduled reports.
        </p>
      ) : (
        <div className="space-y-4">
          {data.schedules.length === 0 ? (
            <p className="text-sm text-ele-body">No reports are scheduled yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.schedules.map((s) => {
                const last = lastRunText(s);
                const isEditing = editing === s.id;
                return (
                  <li key={s.id} className="rounded-ele border border-ele-purple-accent p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-heading text-base text-ele-strong">{s.reportLabel}</p>
                        <p className="text-sm text-ele-body">
                          {s.cadenceLabel}. Draft made out to {s.recipient}.
                        </p>
                      </div>
                      <span className={`font-menu text-xs uppercase tracking-wide ${s.enabled ? "text-ele-green" : "text-ele-body"}`}>{s.enabled ? "On" : "Off"}</span>
                    </div>
                    <p className={`mt-2 text-sm ${last.tone === "warn" ? "text-ele-danger" : "text-ele-body"}`} role={last.tone === "warn" ? "alert" : undefined}>
                      {last.text}
                    </p>
                    {isEditing ? (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="block">
                          <span className={labelCls}>How often</span>
                          <select aria-label="Edit how often" value={editCadence} onChange={(e) => setEditCadence(e.target.value)} className={fieldCls}>
                            {data.cadences.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className={labelCls}>Make the draft out to</span>
                          <input aria-label="Edit recipient" value={editRecipient} onChange={(e) => setEditRecipient(e.target.value)} className={fieldCls} />
                        </label>
                        <div className="flex gap-3 sm:col-span-2">
                          <button
                            type="button"
                            className={saveCls}
                            disabled={busy !== null || !EMAIL.test(editRecipient.trim())}
                            onClick={() =>
                              act(
                                `save:${s.id}`,
                                () => api.reportSchedules.update(s.id, { cadence: editCadence, recipient: editRecipient.trim() }),
                                () => "Schedule saved.",
                                "The schedule was not saved.",
                              ).then(() => setEditing(null))
                            }
                          >
                            Save
                          </button>
                          <button type="button" className={linkBtn} onClick={() => setEditing(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex flex-wrap gap-4">
                        <button
                          type="button"
                          className={linkBtn}
                          disabled={busy !== null}
                          onClick={() => {
                            setEditing(s.id);
                            setEditCadence(s.cadence);
                            setEditRecipient(s.recipient);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={linkBtn}
                          disabled={busy !== null}
                          onClick={() => act(`toggle:${s.id}`, () => api.reportSchedules.update(s.id, { enabled: !s.enabled }), () => (s.enabled ? "Schedule switched off." : "Schedule switched on."), "The schedule was not changed.")}
                        >
                          {s.enabled ? "Switch off" : "Switch on"}
                        </button>
                        <button
                          type="button"
                          className={linkBtn}
                          disabled={busy !== null}
                          onClick={() =>
                            act(
                              `run:${s.id}`,
                              () => api.reportSchedules.run(s.id),
                              (r) => {
                                const res = (r as { result?: { status?: string; message?: string } }).result;
                                return res?.status === "drafted" ? "Draft made in Gmail. Nothing was sent." : `No draft was made. ${res?.message ?? ""}`.trim();
                              },
                              "The draft was not made.",
                            )
                          }
                        >
                          {busy === `run:${s.id}` ? "Making the draft..." : "Make the draft now"}
                        </button>
                        <button type="button" className={`${linkBtn} text-ele-danger`} disabled={busy !== null} onClick={() => { setDeleteError(null); setToDelete(s); }}>
                          Delete
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="rounded-ele border border-ele-purple-accent p-3">
            <h3 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-body">Add a scheduled report</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className={labelCls}>Report</span>
                <select aria-label="Report" value={newType} onChange={(e) => setNewType(e.target.value)} className={fieldCls}>
                  {data.reportTypes.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>How often</span>
                <select aria-label="New schedule how often" value={newCadence} onChange={(e) => setNewCadence(e.target.value)} className={fieldCls}>
                  {data.cadences.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>Make the draft out to</span>
                <input aria-label="New schedule recipient" value={newRecipient} onChange={(e) => setNewRecipient(e.target.value)} placeholder="name@example.co.uk" className={fieldCls} />
              </label>
            </div>
            <div className="mt-3">
              <button
                type="button"
                className={saveCls}
                disabled={busy !== null || !recipientOk || !newType}
                onClick={() =>
                  act(
                    "create",
                    () => api.reportSchedules.create({ reportType: newType, cadence: newCadence, recipient: newRecipient.trim() }),
                    () => "Schedule added. Its first draft is made at the start of the next period.",
                    "The schedule was not added.",
                  ).then(() => setNewRecipient(""))
                }
              >
                Add schedule
              </button>
            </div>
          </div>
          {message ? (
            <p role="status" className="text-sm text-ele-green">
              {message}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          ) : null}
        </div>
      )}
      <ConfirmDeleteModal
        open={toDelete !== null}
        title="Delete this scheduled report?"
        message={toDelete ? `${toDelete.reportLabel}, ${toDelete.cadenceLabel.toLowerCase()}, made out to ${toDelete.recipient}. No more drafts will be made from it. Drafts already in Gmail are left where they are.` : ""}
        confirmLabel="Delete schedule"
        busy={busy === "delete"}
        error={deleteError}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          setBusy("delete");
          setDeleteError(null);
          try {
            await api.reportSchedules.remove(toDelete.id);
            await reload();
            setToDelete(null);
            setMessage("Schedule deleted.");
          } catch (e) {
            setDeleteError(friendlyApiError(e, "The schedule was not deleted."));
          } finally {
            setBusy(null);
          }
        }}
      />
    </section>
  );
}
